import { useState, useEffect, useCallback } from 'react';
import { Download, Upload, AlertCircle, X, Trash2, Loader2, Copy, Check, Database, Cloud, RefreshCw, CloudUpload, Lock, Unlock, Wand2 } from 'lucide-react';
import { usePopup } from '../contexts/PopupContext';
import { useBackup } from '../hooks/useBackup';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const BACKUP_PIN = '969989';

function DataManager({ 
  onExportData, 
  onExportStats, 
  onResetData, 
  onShowImportModal, 
  onImportData,
  userSettings,
  onUpdateSettings,
  onRecoverCategories
}) {
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetConfirmation, setResetConfirmation] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [isCopyingStats, setIsCopyingStats] = useState(false);
  const [statsError, setStatsError] = useState('');
  const { showSuccess, showError, showConfirm } = usePopup();

  const { listBackups, createBackup, downloadBackup, loading: backupLoading } = useBackup();
  const [cloudBackups, setCloudBackups] = useState([]);
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [isUnlocking, setIsUnlocking] = useState(false);

  const isCloudUnlocked = userSettings?.isCloudBackupUnlocked === true;

  const loadBackups = useCallback(async () => {
    if (!isCloudUnlocked) return;
    try {
      const backups = await listBackups();
      setCloudBackups(backups);
    } catch (err) {
      console.error('Errore durante il caricamento dei backup:', err);
    }
  }, [isCloudUnlocked, listBackups]);

  useEffect(() => {
    if (isCloudUnlocked) {
      loadBackups();
    }
  }, [isCloudUnlocked, loadBackups]);

  const handleUnlockCloudBackup = async () => {
    if (pinInput === BACKUP_PIN) {
      try {
        setIsUnlocking(true);
        await onUpdateSettings({
          ...userSettings,
          isCloudBackupUnlocked: true
        });
        showSuccess('Backup Online sbloccato correttamente!', 'Sbloccato');
      } catch (err) {
        showError('Errore durante il salvataggio delle impostazioni.');
      } finally {
        setIsUnlocking(false);
      }
    } else {
      showError('PIN errato. Riprova.');
      setPinInput('');
    }
  };

  const handleCreateCloudBackup = async () => {
    try {
      setIsCreatingBackup(true);
      const data = await onExportData();
      await createBackup(data);
      showSuccess('Backup creato con successo nel Database', 'Backup Cloud');
      loadBackups();
    } catch (err) {
      console.error(err);
      showError('Errore durante la creazione del backup cloud');
    } finally {
      setIsCreatingBackup(false);
    }
  };

  const handleRestoreCloudBackup = async (backupId) => {
    const confirmed = await new Promise((resolve) => {
      showConfirm(
        <div>
          <p className="mb-3">Sei sicuro di voler ripristinare questo backup dal cloud?</p>
          <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
            I dati attuali verranno sovrascritti con quelli del backup selezionato.
          </p>
          <p className="font-medium text-red-600">Questa azione non può essere annullata.</p>
        </div>,
        'Ripristina Backup Cloud',
        () => resolve(true),
        () => resolve(false)
      );
    });

    if (!confirmed) return;

    try {
      const data = await downloadBackup(backupId);
      if (typeof onImportData === 'function') {
        await onImportData(data);
        showSuccess('Dati ripristinati correttamente dal cloud', 'Ripristino Completato');
      } else {
        throw new Error('Funzione di importazione non disponibile');
      }
    } catch (err) {
      console.error(err);
      showError('Errore durante il ripristino dal cloud: ' + err.message);
    }
  };

  // Helper per la copia negli appunti con fallback per browser/contesti limitati
  const copyToClipboard = async (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (err) {
        console.warn('Navigator clipboard failed, trying fallback', err);
      }
    }

    try {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      textArea.style.top = "0";
      textArea.setAttribute('readonly', ''); // Previene tastiera su mobile
      document.body.appendChild(textArea);
      textArea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      return successful;
    } catch (err) {
      console.error('Fallback copy failed', err);
      return false;
    }
  };

  const handleExportData = async () => {
    if (typeof onExportData !== 'function') {
      setExportError('Esportazione non disponibile in questo momento.');
      return;
    }

    setExportError('');
    setIsExporting(true);

    let downloadLink = null;
    let objectUrl = '';

    try {
      const exportedPayload = await onExportData();
      if (!exportedPayload || typeof exportedPayload !== 'object') {
        throw new Error('Dati da esportare non validi.');
      }

      const exportedAt = exportedPayload.metadata?.exportedAt || new Date().toISOString();
      const formattedDate = exportedAt.split('T')[0] || 'backup';
      const fileName = `expense-tracker-backup-${formattedDate}.json`;

      const blob = new Blob([JSON.stringify(exportedPayload, null, 2)], { type: 'application/json' });
      objectUrl = URL.createObjectURL(blob);

      downloadLink = document.createElement('a');
      downloadLink.href = objectUrl;
      downloadLink.download = fileName;
      document.body.appendChild(downloadLink);
      downloadLink.click();
    } catch (error) {
      console.error('Errore durante l\'esportazione dei dati:', error);
      setExportError(error.message || 'Errore durante l\'esportazione dei dati.');
    } finally {
      if (downloadLink && downloadLink.parentNode) {
        document.body.removeChild(downloadLink);
      }
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
      setIsExporting(false);
    }
  };

  const handleCopyStats = async () => {
    if (typeof onExportStats !== 'function') {
      showError('Errore interno: funzione di esportazione non trovata.');
      return;
    }

    try {
      setStatsError('');
      setIsCopyingStats(true);
      const text = await onExportStats();
      
      if (!text || typeof text !== 'string') {
        throw new Error('Il riepilogo generato è vuoto o non valido.');
      }

      const success = await copyToClipboard(text);
      
      if (success) {
        setTimeout(() => {
          setIsCopyingStats(false);
        }, 2000);
      } else {
        throw new Error('Il sistema ha negato l\'accesso agli appunti.');
      }
    } catch (error) {
      console.error('Errore durante la copia delle statistiche:', error);
      setStatsError(error.message || 'Errore durante la copia.');
      showError(error.message || 'Impossibile copiare il riepilogo.', 'Errore Copia');
      setIsCopyingStats(false);
    }
  };

  const openResetConfirm = () => {
    setShowResetConfirm(true);
    setResetConfirmation('');
  };

  const closeResetConfirm = () => {
    setShowResetConfirm(false);
    setResetConfirmation('');
  };

  const handleConfirmReset = async () => {
    if (resetConfirmation !== 'RESETTA' || typeof onResetData !== 'function') {
      return;
    }

    try {
      setIsResetting(true);
      await onResetData();
    } finally {
      setIsResetting(false);
      closeResetConfirm();
    }
  };

  return (
    <div className="space-y-6 mt-2">
      {/* Cloud Backup Section */}
      <div className="space-y-3 bg-blue-50/50 dark:bg-blue-900/10 p-4 rounded-2xl border border-blue-100/50 dark:border-blue-900/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
            <Cloud className="w-5 h-5" />
            <h3 className="font-bold text-sm">Backup Online</h3>
          </div>
          {isCloudUnlocked && (
            <button 
              onClick={loadBackups}
              disabled={backupLoading}
              className="p-1.5 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${backupLoading ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>

        {!isCloudUnlocked ? (
          <div className="space-y-3 py-2">
            <div className="flex items-center gap-2 text-xs text-gray-500 italic">
              <Lock className="w-3 h-3" />
              Inserisci il PIN per sbloccare i backup automatici online.
            </div>
            <div className="flex gap-2">
              <input
                type="password"
                maxLength={6}
                value={pinInput}
                onChange={(e) => setPinInput(e.target.value)}
                placeholder="PIN 6 cifre"
                className="flex-1 p-2 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-center tracking-widest"
              />
              <button
                onClick={handleUnlockCloudBackup}
                disabled={isUnlocking || pinInput.length < 6}
                className="px-4 py-2 bg-blue-600 text-white rounded-xl text-xs font-bold hover:bg-blue-700 transition-all disabled:opacity-50"
              >
                {isUnlocking ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sblocca'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {cloudBackups.length > 0 ? (
                cloudBackups.map((backup) => (
                  <div key={backup.id} className="flex items-center justify-between bg-white dark:bg-gray-800 p-2.5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
                    <div className="flex flex-col">
                      <span className="text-xs font-bold text-gray-700 dark:text-gray-300">
                        Backup del {backup.backupName || format(new Date(backup.updatedAt), 'dd/MM/yyyy', { locale: it })}
                      </span>
                      <span className="text-[10px] text-gray-500">
                        {format(new Date(backup.updatedAt), 'HH:mm', { locale: it })}
                      </span>
                    </div>
                    <button
                      onClick={() => handleRestoreCloudBackup(backup.id)}
                      disabled={backupLoading}
                      className="px-3 py-1.5 bg-blue-600 text-white text-[10px] font-bold rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1"
                    >
                      <Upload className="w-3 h-3" />
                      Ripristina
                    </button>
                  </div>
                ))
              ) : (
                <div className="text-center py-4 text-xs text-gray-500 italic">
                  Nessun backup trovato nel database.
                </div>
              )}
            </div>

            <button
              onClick={handleCreateCloudBackup}
              disabled={isCreatingBackup || backupLoading}
              className="w-full flex items-center justify-center gap-2 py-2.5 bg-blue-600 text-white rounded-xl text-xs font-bold hover:bg-blue-700 transition-all disabled:opacity-50"
            >
              {isCreatingBackup ? <Loader2 className="w-4 h-4 animate-spin" /> : <CloudUpload className="w-4 h-4" />}
              Esegui Backup Ora
            </button>
          </>
        )}
      </div>

      {/* Backup and Import Group */}
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <button
              type="button"
              onClick={handleExportData}
              disabled={isExporting}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl shadow-sm hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200 transform hover:scale-105 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              <span className="font-semibold text-sm">Scarica</span>
            </button>
            <p className="text-[10px] text-center text-gray-500 dark:text-gray-400 px-1">
              Esporta file JSON locale
            </p>
          </div>

          <div className="space-y-1.5">
            <button
              type="button"
              onClick={onShowImportModal}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl shadow-sm hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200 transform hover:scale-105"
            >
              <Upload className="w-4 h-4" />
              <span className="font-semibold text-sm">Importa</span>
            </button>
            <p className="text-[10px] text-center text-gray-500 dark:text-gray-400 px-1">
              Carica file JSON locale
            </p>
          </div>
        </div>
      </div>

      {/* Category Recovery Utility */}
      {onRecoverCategories && (
        <button
          type="button"
          onClick={onRecoverCategories}
          className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-xl shadow-md transition-all duration-200 transform hover:scale-[1.02] cursor-pointer"
        >
          <Wand2 className="w-4 h-4" />
          <span className="font-semibold text-sm">Recupera Categorie Mancanti</span>
        </button>
      )}

      {/* Copy Stats and Reset Group */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <button
            type="button"
            onClick={handleCopyStats}
            disabled={isCopyingStats}
            className={`w-full flex items-center justify-center gap-3 px-4 py-3 backdrop-blur-sm text-white rounded-xl shadow-lg transition-all duration-200 transform hover:scale-105 disabled:transform-none ${
              isCopyingStats 
                ? 'bg-green-600/90' 
                : 'bg-purple-600/90 hover:bg-purple-700/90'
            }`}
          >
            {isCopyingStats ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            <span className="font-semibold text-sm">Copia Riepilogo</span>
          </button>
          <p className="text-[10px] text-center text-gray-500 dark:text-gray-400 px-1">
            Riepilogo per il consulente
          </p>
        </div>

        <div className="space-y-1.5">
          <button
            type="button"
            onClick={openResetConfirm}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-red-600/90 backdrop-blur-sm text-white rounded-xl shadow-lg hover:bg-red-700/90 transition-all duration-200 transform hover:scale-105"
          >
            <Trash2 className="w-4 h-4" />
            <span className="font-semibold text-sm">Reset Completo</span>
          </button>
          <p className="text-[10px] text-center text-red-500/80 dark:text-red-400/80 px-1">
            Elimina definitivamente tutto
          </p>
        </div>
      </div>

      {/* Messaggi di errore compatti */}
      {(exportError || statsError) && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-100 dark:border-red-900/30">
          {exportError && (
            <p className="text-xs text-red-600 dark:text-red-400 font-medium whitespace-pre-line">
              • {exportError}
            </p>
          )}
          {statsError && (
            <p className="text-xs text-red-600 dark:text-red-400 font-medium mt-1 whitespace-pre-line">
              • {statsError}
            </p>
          )}
        </div>
      )}
    
      {/* Modale conferma reset completo */}
      {showResetConfirm && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={closeResetConfirm}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full animate-fade-in-up"
            onClick={e => e.stopPropagation()}
          >
            <div className="bg-red-600/90 backdrop-blur-sm text-white p-6 rounded-t-2xl flex items-center justify-between">
              <button
                type="button"
                onClick={closeResetConfirm}
                className="text-white/80 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Trash2 className="w-5 h-5" />
                Reset Completo
              </h2>
              <div className="w-6"></div>
            </div>
            <div className="p-6 space-y-6">
              <div className="text-center">
                <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  ATTENZIONE!
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Stai per eliminare <strong>DEFINITIVAMENTE</strong> tutti i tuoi dati:
                </p>
                <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1 mb-6">
                  <li>• Tutte le spese e entrate</li>
                  <li>• Tutte le categorie personalizzate</li>
                  <li>• Tutti i negozi salvati</li>
                  <li>• Tutti i conti e saldi</li>
                  <li>• Tutte le impostazioni</li>
                </ul>
                <p className="text-red-600 font-semibold">
                  Questa azione è <strong>IRREVERSIBILE</strong> e non può essere annullata!
                </p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  Per confermare, scrivi <strong>RESETTA</strong>:
                </label>
                <input
                  type="text"
                  value={resetConfirmation}
                  onChange={e => setResetConfirmation(e.target.value)}
                  placeholder="Scrivi RESETTA per continuare"
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={closeResetConfirm}
                  className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-xl font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-all duration-200"
                >
                  Annulla
                </button>
                <button
                  type="button"
                  onClick={handleConfirmReset}
                  disabled={resetConfirmation !== 'RESETTA' || isResetting}
                  className="flex-1 px-4 py-2 bg-red-600/90 backdrop-blur-sm text-white rounded-xl font-medium hover:bg-red-700/90 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
                >
                  {isResetting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Resettando...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Reset Completo
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataManager;
